/**
 * Server state for Rules (beta): the catalog, the brand's rules and every write to them. Each key
 * carries the brand, so two brands never read each other's rules; a write refreshes the list and
 * the open rule.
 */

import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { isAuthenticatedAtom } from '@/hooks/use-auth';
import { ticketRulesService } from '@/services/ticket-rules.service';
import type { CreateRuleRequest, RuleRecord, RuleStatus, TestRequest, UpdateRuleRequest } from '@/types/rules.types';

export const TICKET_RULES_KEY = 'ticket-rules';

const listKey = (bu: string) => [TICKET_RULES_KEY, 'list', bu];

const refreshRules = () => Promise.all([invalidateQueries([TICKET_RULES_KEY, 'list']), invalidateQueries([TICKET_RULES_KEY, 'detail'])]);

// The catalog only changes with a deploy.
const catalogQueryAtom = atomWithQuery((get) => ({
	queryKey: [TICKET_RULES_KEY, 'catalog', buScopeKey(get)],
	queryFn: () => ticketRulesService.catalog(buScopeParams(get)),
	staleTime: 30 * 60 * 1000,
	enabled: get(isAuthenticatedAtom)
}));

/** What the builder can offer: triggers, facts, operators, actions, outcomes and templates. */
export function useTicketRulesCatalog() {
	return useAtomValue(catalogQueryAtom);
}

const listQueryAtom = atomWithQuery((get) => ({
	queryKey: listKey(buScopeKey(get)),
	queryFn: () => ticketRulesService.list(buScopeParams(get)),
	enabled: get(isAuthenticatedAtom)
}));

/** The selected brand's rules, in the order they run. */
export function useTicketRules() {
	return useAtomValue(listQueryAtom);
}

const ruleQueryAtom = (id: string) =>
	atomWithQuery((get) => ({
		queryKey: [TICKET_RULES_KEY, 'detail', buScopeKey(get), id],
		queryFn: () => ticketRulesService.get(id, buScopeParams(get)),
		enabled: get(isAuthenticatedAtom) && Boolean(id)
	}));

/** One rule; pass an empty id for none (a rule not saved yet). */
export function useTicketRule(id: string) {
	const atom = useMemo(() => ruleQueryAtom(id), [id]);
	return useAtomValue(atom);
}

// Writes are not retried: a 400 or 403 is the answer, and a second create would make a second rule.
const createMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'create'],
	mutationFn: (body: CreateRuleRequest) => ticketRulesService.create(body, buScopeParams(get)),
	retry: false,
	onSuccess: refreshRules
}));

export function useCreateTicketRule() {
	return useAtomValue(createMutationAtom);
}

const updateMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'update'],
	mutationFn: ({ id, body }: { id: string; body: UpdateRuleRequest }) => ticketRulesService.update(id, body, buScopeParams(get)),
	retry: false,
	onSuccess: refreshRules
}));

export function useUpdateTicketRule() {
	return useAtomValue(updateMutationAtom);
}

/** Applies a change to the cached list at once, so a switch or a drag answers without waiting. */
const patchList = (bu: string, patch: (rules: RuleRecord[]) => RuleRecord[]) =>
	queryClient.setQueryData<RuleRecord[]>(listKey(bu), (rules) => (rules ? patch(rules) : rules));

const statusMutationAtom = atomWithMutation((get) => {
	const bu = buScopeKey(get);
	return {
		mutationKey: [TICKET_RULES_KEY, 'status'],
		mutationFn: ({ id, status }: { id: string; status: RuleStatus }) => ticketRulesService.setStatus(id, status, buScopeParams(get)),
		retry: false,
		onMutate: ({ id, status }: { id: string; status: RuleStatus }) =>
			patchList(bu, (rules) => rules.map((rule) => (rule.id === id ? { ...rule, status } : rule))),
		onSettled: refreshRules
	};
});

export function useSetTicketRuleStatus() {
	return useAtomValue(statusMutationAtom);
}

const reorderMutationAtom = atomWithMutation((get) => {
	const bu = buScopeKey(get);
	return {
		mutationKey: [TICKET_RULES_KEY, 'reorder'],
		mutationFn: (ids: string[]) => ticketRulesService.reorder(ids, buScopeParams(get)),
		retry: false,
		// Order decides which playbook wins, so a refetch already in flight must not paint an older order
		// over the one just chosen. The list sends one move at a time (see RulesList).
		onMutate: async (ids: string[]) => {
			await queryClient.cancelQueries({ queryKey: listKey(bu) });
			patchList(bu, (rules) => {
				const byId = new Map(rules.map((rule) => [rule.id, rule]));
				return ids.map((id) => byId.get(id)).filter((rule): rule is RuleRecord => rule !== undefined);
			});
		},
		onSettled: refreshRules
	};
});

export function useReorderTicketRules() {
	return useAtomValue(reorderMutationAtom);
}

const duplicateMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'duplicate'],
	mutationFn: (id: string) => ticketRulesService.duplicate(id, buScopeParams(get)),
	retry: false,
	onSuccess: refreshRules
}));

export function useDuplicateTicketRule() {
	return useAtomValue(duplicateMutationAtom);
}

const deleteMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'delete'],
	mutationFn: (id: string) => ticketRulesService.remove(id, buScopeParams(get)),
	retry: false,
	onSuccess: refreshRules
}));

export function useDeleteTicketRule() {
	return useAtomValue(deleteMutationAtom);
}

// A dry run reads facts (an intent costs a model call); the app-wide retry would run it twice.
const testMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'test'],
	mutationFn: (request: TestRequest) => ticketRulesService.test(request, buScopeParams(get)),
	retry: false
}));

export function useTestTicketRules() {
	return useAtomValue(testMutationAtom);
}

// ── Migrate and roll back ────────────────────────────────────────────────────────────────────────

const migrationQueryAtom = atomWithQuery((get) => ({
	queryKey: [TICKET_RULES_KEY, 'migration', buScopeKey(get)],
	queryFn: () => ticketRulesService.migrationStatus(buScopeParams(get)),
	enabled: get(isAuthenticatedAtom) && Boolean(buScopeParams(get).bu_id)
}));

/** Whether playbooks are on for the selected brand, what migrating would create, and the history. */
export function useAutopilotMigration() {
	return useAtomValue(migrationQueryAtom);
}

const afterMove = () =>
	Promise.all([invalidateQueries(['account', 'flags']), invalidateQueries([TICKET_RULES_KEY])]);

// Never retried: a slow success must not be what decides whether the move happened twice.
const migrateMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'migrate'],
	mutationFn: () => ticketRulesService.migrate(buScopeParams(get)),
	retry: false,
	onSettled: afterMove
}));

export function useMigrateToAutopilot() {
	return useAtomValue(migrateMutationAtom);
}

const rollBackMutationAtom = atomWithMutation((get) => ({
	mutationKey: [TICKET_RULES_KEY, 'rollback'],
	mutationFn: () => ticketRulesService.rollBack(buScopeParams(get)),
	retry: false,
	onSettled: afterMove
}));

export function useRollBackAutopilot() {
	return useAtomValue(rollBackMutationAtom);
}
